import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { DataPoint, MetricType } from '../../pages/Dashboard';
import { EditDialog } from './EditDialog';
import { Info, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { CompositeBar } from './CompositeBar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface DashboardChartProps {
  data: DataPoint[];
  selectedMetric: MetricType;
  onUpdateDataPoint: (id: string, value: number) => void;
  netImpact: number;
  selectedSupplier: string;
}

export function DashboardChart({ data, selectedMetric, onUpdateDataPoint, netImpact, selectedSupplier }: DashboardChartProps) {
  const [editingItem, setEditingItem] = useState<DataPoint | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Supplier percentages for potential impact calculation
  // Logic: Selecting "All" means integrating all 30 non-integrated suppliers = 100% impact
  // Individual suppliers show proportional impact based on their size/importance
  const supplierPercentages: { [key: string]: number } = {
    'All': 1.0,  // Maximum: integrating all 30 non-integrated suppliers
    'Sunrise Flour Mills': 0.92,  // Large supplier - significant impact
    'SweetGlaze Sugar Co.': 0.88,
    'HappyHen Egg Farms': 0.85,
    'Crave Dairy Distributors': 0.90,
    'BrewMasters Coffee Roasters': 0.86,
    'FreshPack Paper Goods': 0.84,
    'Others': 0.95  // Represents multiple smaller suppliers combined
  };

  const supplierMultiplier = supplierPercentages[selectedSupplier] || 1.0;

  // Define potential impact values (with all non-integrated suppliers)
  // For CO2e: going from -1340 to -1800 means 460 additional reduction
  const potentialImpacts: { [key: string]: { co2e: number; waste: number; treesSaved: number } } = {
    'chep': {
      co2e: -1800,        // Potential with all suppliers
      waste: -3240,        // -1800 × 1.8
      treesSaved: 90      // 1800 × 0.05
    },
    'supplier': {
      co2e: -1200,
      waste: -3600,        // -1200 × 3.0
      treesSaved: 60
    },
    'cycle': {
      co2e: -350,
      waste: -280,         // -350 × 0.8
      treesSaved: 17.5
    },
    'transport': {
      // Transport Collaboration has NEGATIVE impact with non-integrated suppliers
      // Current: -1 tCO2e (reduction), Potential: +5 tCO2e (increase - worse for environment)
      co2e: 5,            // Goes from -1 to +5 (negative environmental impact)
      waste: 6,            // Goes from -1.2 to +6 (negative environmental impact)
      treesSaved: 0.5      // Goes from 0.05 to 0.5 (positive - more trees saved)
    }
  };

  const getPotentialValue = (item: DataPoint) => {
    const potential = potentialImpacts[item.id];
    if (!potential) return getMetricValue(item);

    // Remove potential bar for CHEP Pallets in Trees Saved tab
    if (item.id === 'chep' && selectedMetric === 'TreesSaved') {
      return getMetricValue(item);
    }

    const currentValue = getMetricValue(item);
    const potentialFullValue = selectedMetric === 'CO2e' ? potential.co2e :
      selectedMetric === 'Waste' ? potential.waste :
        potential.treesSaved;

    // Calculate the increase from current to potential
    const potentialIncrease = (potentialFullValue - currentValue) * supplierMultiplier;
    return currentValue + potentialIncrease;
  };

  const getMetricValue = (item: DataPoint) => {
    switch (selectedMetric) {
      case 'CO2e':
        return item.co2e;
      case 'Waste':
        return item.waste;
      case 'TreesSaved':
        return item.treesSaved;
      default:
        return item.co2e;
    }
  };

  const getMetricUnit = () => {
    switch (selectedMetric) {
      case 'CO2e':
        return 'tCO₂e';
      case 'Waste':
        return 'kg';
      case 'TreesSaved':
        return 'trees';
      default:
        return 'tCO₂e';
    }
  };

  const getBarColor = (value: number, isNet: boolean = false) => {
    // For Trees Saved: positive = good (green), negative = bad (red) - including net impact
    if (selectedMetric === 'TreesSaved') {
      if (value > 0) return '#8CC63F'; // Green for trees saved
      return '#D71920'; // Red for trees lost
    }

    // For CO2e and Waste: show green for net impact if it's positive (reduction)
    // Otherwise blue for neutral net impact, negative = good (green), positive = bad (red)
    if (isNet) {
      return value < 0 ? '#8CC63F' : '#0072CE'; // Green for positive net impact, blue otherwise
    }
    if (value < 0) return '#8CC63F'; // Green for reduction
    return '#D71920'; // Red for increase
  };

  const chartData = [
    ...data.map(item => {
      const val = getMetricValue(item);
      const potVal = getPotentialValue(item);

      // Log for transport and debugging
      if (item.id === 'transport') {
        console.log('Transport Collaboration:', {
          name: item.name,
          value: val,
          potentialValue: potVal,
          selectedMetric,
          supplierMultiplier
        });
      }

      return {
        name: item.name,
        value: val,
        potentialValue: potVal,
        fullData: item,
        isNet: false
      };
    }),
    {
      name: 'Net Impact',
      value: netImpact,
      potentialValue: data.reduce((sum, item) => sum + getPotentialValue(item), 0),
      fullData: null,
      isNet: true
    }
  ];

  console.log('Net Impact calculation:', {
    netImpact,
    potentialNetImpact: data.reduce((sum, item) => sum + getPotentialValue(item), 0),
    selectedMetric,
    individualPotentials: data.map(item => ({
      name: item.name,
      potential: getPotentialValue(item)
    }))
  });

  const handleBarClick = (entry: any) => {
    if (entry.fullData) {
      setEditingItem(entry.fullData);
      setIsDialogOpen(true);
    }
  };

  const handleUpdateValue = (newValue: number) => {
    if (editingItem) {
      onUpdateDataPoint(editingItem.id, newValue);
    }
    setIsDialogOpen(false);
    setEditingItem(null);
  };

  // Export functions
  const exportToCSV = (metric: MetricType) => {
    const metricName = metric === 'CO2e' ? 'CO2e' : metric === 'Waste' ? 'Waste' : 'Trees Saved';
    const unit = metric === 'CO2e' ? 'tCO₂e' : metric === 'Waste' ? 'kg' : 'trees';

    // Create CSV header
    let csv = `Initiative,${metricName} (${unit}),Description\n`;

    // Add data rows
    data.forEach(item => {
      const value = metric === 'CO2e' ? item.co2e : metric === 'Waste' ? item.waste : item.treesSaved;
      const description = item.description.replace(/,/g, ';'); // Replace commas to avoid CSV issues
      csv += `"${item.name}",${value},"${description}"\n`;
    });

    // Add net impact row
    const netValue = data.reduce((sum, item) => {
      const val = metric === 'CO2e' ? item.co2e : metric === 'Waste' ? item.waste : item.treesSaved;
      return sum + val;
    }, 0);
    csv += `"Net Impact",${netValue},"Total net environmental impact for the period"\n`;

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `P&G_Impact_${metricName}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = (metric: MetricType) => {
    exportToCSV(metric);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload[0]) {
      const data = payload[0].payload;
      const value = payload[0].value;
      const potentialValue = data.potentialValue;

      // Determine if this is a positive environmental impact
      // For Trees Saved: positive value = positive impact
      // For CO2e and Waste: negative value = positive impact
      const isPositiveImpact = selectedMetric === 'TreesSaved' ? value > 0 : value < 0;

      // Determine tooltip color based on metric type
      const getTooltipColor = () => {
        if (selectedMetric === 'TreesSaved') {
          return value > 0 ? 'text-[#8CC63F]' : value < 0 ? 'text-[#D71920]' : 'text-[#0072CE]';
        }
        return value < 0 ? 'text-[#8CC63F]' : value > 0 ? 'text-[#D71920]' : 'text-[#0072CE]';
      };

      // Calculate potential impact information
      const hasPotential = potentialValue !== null && potentialValue !== undefined;
      const potentialDifference = hasPotential ? potentialValue - value : 0;
      const showPotential = hasPotential && Math.abs(potentialValue) > Math.abs(value);

      // Determine if potential is positive or negative environmental impact
      const getPotentialImpactText = () => {
        if (!showPotential) return null;

        // Special case for Transport Collaboration (crosses zero)
        if (value < 0 && potentialValue > 0) {
          return {
            text: 'Additional environmental cost with non-integrated suppliers',
            impact: 'negative',
            color: 'text-red-600'
          };
        }

        if (selectedMetric === 'TreesSaved') {
          // Trees Saved: more trees = better
          const additionalTrees = potentialDifference;
          return {
            text: `${Math.abs(additionalTrees).toFixed(1)} more trees could be saved`,
            impact: 'positive',
            color: 'text-green-600'
          };
        } else {
          // CO2e and Waste: more reduction = better
          const additionalReduction = Math.abs(potentialDifference);
          return {
            text: `${additionalReduction.toFixed(1)} ${getMetricUnit()} additional reduction possible`,
            impact: 'positive',
            color: 'text-green-600'
          };
        }
      };

      const potentialInfo = getPotentialImpactText();

      return (
        <div className="bg-white/95 backdrop-blur-xl p-4 rounded-xl shadow-[0_10px_40px_rgb(0,0,0,0.15)] border border-white/40 max-w-xs">
          <p className="text-gray-900 mb-2">{data.name}</p>
          <p className={`text-lg mb-2 ${getTooltipColor()}`}>
            {value > 0 ? '+' : ''}{value.toLocaleString()} {getMetricUnit()}
          </p>

          {/* Potential impact information */}
          {showPotential && potentialInfo && (
            <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs text-gray-700 mb-1">
                <span className="font-semibold">Potential by integrating non-integrated suppliers:</span>
              </p>
              <p className={`text-sm ${potentialInfo.color} font-medium`}>
                {value > 0 ? '+' : ''}{potentialValue.toLocaleString()} {getMetricUnit()}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {potentialInfo.text}
              </p>
            </div>
          )}

          {data.fullData && (
            <>
              <p className="text-sm text-gray-600 mb-2">{data.fullData.description}</p>
              <p className="text-xs text-gray-500">
                {isPositiveImpact ? '✓ Positive environmental impact' : '✗ Negative environmental impact'}
              </p>
              <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                <Info className="w-3 h-3" />
                Click to edit value
              </p>
            </>
          )}
          {data.isNet && (
            <>
              <p className="text-sm text-gray-600 mb-2">
                Total net environmental impact for the period
              </p>
              {showPotential && potentialInfo && (
                <p className="text-xs text-blue-600 mt-1">
                  Lighter bar shows potential from non-integrated suppliers
                </p>
              )}
            </>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:shadow-[0_20px_60px_rgb(0,0,0,0.15)] transition-all duration-500 p-8 border border-white/20 relative overflow-hidden group">
        {/* Premium gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-purple-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

        <div className="mb-6 relative z-10">
          <h2 className="text-gray-900 mb-1">12-Month Impact Analysis</h2>
          <p className="text-sm text-gray-600">Click any bar to simulate changes in {getMetricUnit()}</p>
        </div>

        <div className="relative z-10">
          <ResponsiveContainer width="100%" height={500}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fill: '#374151', fontSize: 12 }}
              />
              <YAxis
                tick={{ fill: '#374151' }}
                label={{ value: getMetricUnit(), angle: -90, position: 'insideLeft', style: { fill: '#374151' } }}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 114, 206, 0.1)' }} />
              <ReferenceLine y={0} stroke="#9ca3af" strokeWidth={2} />
              <Bar
                dataKey="value"
                radius={[8, 8, 0, 0]}
                onClick={handleBarClick}
                cursor="pointer"
                shape={(props: any) => <CompositeBar {...props} />}
              >
                {chartData.map((entry, index) => {
                  const isGreenNet = entry.isNet && selectedMetric !== 'TreesSaved' && entry.value < 0;
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={getBarColor(entry.value, entry.isNet)}
                      opacity={entry.isNet ? 1 : 0.9}
                      strokeWidth={entry.isNet ? 3 : 0}
                      stroke={isGreenNet ? '#0072CE' : entry.isNet ? '#0072CE' : undefined}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex justify-center gap-6 mt-6 relative z-10">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#8CC63F' }}></div>
            <span className="text-sm text-gray-600">Reduction (Positive Impact)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#D71920' }}></div>
            <span className="text-sm text-gray-600">Increase (Negative Impact)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2" style={{ backgroundColor: '#0072CE', borderColor: '#0072CE' }}></div>
            <span className="text-sm text-gray-600">Net Impact</span>
          </div>
        </div>

        {/* Export Button */}
        <div className="absolute border right-4 rounded-lg top-4">
          <TooltipProvider>
            <DropdownMenu>
              <UITooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button size="icon" variant="outline">
                      <Download className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Export</p>
                </TooltipContent>
              </UITooltip>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('CO2e')}>
                  Export CO2e Data
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('Waste')}>
                  Export Waste Data
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('TreesSaved')}>
                  Export Trees Saved Data
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TooltipProvider>
        </div>
      </div>

      {editingItem && (
        <EditDialog
          isOpen={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
            setEditingItem(null);
          }}
          item={editingItem}
          currentValue={getMetricValue(editingItem)}
          metric={selectedMetric}
          unit={getMetricUnit()}
          onSave={handleUpdateValue}
        />
      )}
    </>
  );
}
